/**
 * Category Registry Service (SSOT)
 * ================================
 * FINLIST SSOT: Unified category-envelope atomic wiring (global-scale perf target)
 * 
 * This service provides the SINGLE SOURCE OF TRUTH for all categories in FinList.
 * All pages MUST read from this registry for category dropdowns/selection.
 * Category identity links across the app must always use `categoryId` and never name strings or slugs.
 * 
 * Firestore Path: `users/{uid}/categories/{categoryId}`
 * 
 * @version 1.0.0
 * @date 2025-11-28
 */

import { db, auth } from '@/config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import logger from '@/utils/logger';
// CATEGORY CORE: Single source of truth for category normalization
import { 
  normalizeToCategoryTaxonomyId, 
  normalizeCategoryWithFallback 
} from '@/constants/categoryCore';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Category types - canonical reference
 */
export const CATEGORY_TYPES = {
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  SAVINGS: 'SAVINGS',
  TASK: 'TASK',
  SHOPPING: 'SHOPPING'
};

/**
 * Category groups for UI organization
 */
export const CATEGORY_GROUPS = {
  FOOD: 'Food',
  BILLS: 'Bills',
  LIFESTYLE: 'Lifestyle',
  TRANSPORT: 'Transport',
  HOUSING: 'Housing',
  HEALTH: 'Health',
  EDUCATION: 'Education',
  INCOME_SOURCES: 'Income Sources',
  SAVINGS_GOALS: 'Savings Goals',
  OTHER: 'Other'
};

// ============================================================================
// FIRESTORE REFERENCES
// ============================================================================

/**
 * Get user's category registry collection reference
 * FINLIST SSOT: Central Category Registry
 * @param {string} userId 
 * @returns {CollectionReference}
 */
function getCategoryRegistryRef(userId) {
  if (!userId) throw new Error('[CATEGORY REGISTRY] User ID required');
  return collection(db, 'users', userId, 'categories');
}

/**
 * Get specific category document reference
 * @param {string} userId 
 * @param {string} categoryId 
 * @returns {DocumentReference}
 */
function getCategoryDocRef(userId, categoryId) {
  if (!userId) throw new Error('[CATEGORY REGISTRY] User ID required');
  if (!categoryId) throw new Error('[CATEGORY REGISTRY] Category ID required');
  return doc(db, 'users', userId, 'categories', categoryId);
}

// ============================================================================
// DEFAULT CATEGORY DEFINITIONS
// ============================================================================

/**
 * Default expense categories - seeded on first use
 * FINLIST SSOT: These become Firestore documents, not in-code constants
 */
export const DEFAULT_EXPENSE_CATEGORIES = [
  {
    id: 'cat_groceries',
    name: 'Groceries & Food',  // SSOT: Unified with categoryTaxonomy.js canonical name
    slug: 'groceries',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.FOOD,
    icon: 'ShoppingCart',
    color: '#10b981',
    sortOrder: 1,
    isActive: true
  },
  {
    id: 'cat_dining',
    name: 'Dining Out',
    slug: 'dining',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.FOOD,
    icon: 'Coffee',
    color: '#f59e0b',
    sortOrder: 2,
    isActive: true
  },
  {
    id: 'cat_household',
    name: 'Household',
    slug: 'household',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.HOUSING,
    icon: 'Home',
    color: '#3b82f6',
    sortOrder: 3,
    isActive: true
  },
  {
    id: 'cat_utilities',
    name: 'Utilities & Bills',  // SSOT: Unified with categoryTaxonomy.js canonical name
    slug: 'utilities',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.BILLS,
    icon: 'Zap',
    color: '#f59e0b',
    sortOrder: 4,
    isActive: true
  },
  {
    id: 'cat_transportation',
    name: 'Transportation',
    slug: 'transportation',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.TRANSPORT,
    icon: 'Car',
    color: '#8b5cf6',
    sortOrder: 5,
    isActive: true
  },
  {
    id: 'cat_healthcare',
    name: 'Healthcare',
    slug: 'healthcare',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.HEALTH,
    icon: 'Heart',
    color: '#ef4444',
    sortOrder: 6,
    isActive: true
  },
  {
    id: 'cat_entertainment',
    name: 'Entertainment',
    slug: 'entertainment',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.LIFESTYLE,
    icon: 'Sparkles',
    color: '#ec4899',
    sortOrder: 7,
    isActive: true
  },
  {
    id: 'cat_shopping',
    name: 'Shopping',
    slug: 'shopping',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.LIFESTYLE,
    icon: 'ShoppingBag',
    color: '#06b6d4',
    sortOrder: 8,
    isActive: true
  },
  {
    id: 'cat_subscriptions',
    name: 'Subscriptions',
    slug: 'subscriptions',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.BILLS,
    icon: 'CreditCard',
    color: '#6366f1',
    sortOrder: 9,
    isActive: true
  },
  {
    id: 'cat_education',
    name: 'Education & Learning',  // SSOT: Unified with categoryTaxonomy.js canonical name
    slug: 'education',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.EDUCATION,
    icon: 'BookOpen',
    color: '#0ea5e9',
    sortOrder: 10,
    isActive: true
  },
  {
    id: 'cat_personal_care',
    name: 'Personal Care',
    slug: 'personal-care',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.HEALTH,
    icon: 'User',
    color: '#ec4899',
    sortOrder: 11,
    isActive: true
  },
  {
    id: 'cat_insurance',
    name: 'Insurance',
    slug: 'insurance',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.BILLS,
    icon: 'Shield',
    color: '#14b8a6',
    sortOrder: 12,
    isActive: true
  },
  {
    id: 'cat_gifts',
    name: 'Gifts & Donations',
    slug: 'gifts',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.LIFESTYLE,
    icon: 'Gift',
    color: '#f43f5e',
    sortOrder: 13,
    isActive: true
  },
  {
    id: 'cat_travel',
    name: 'Travel',
    slug: 'travel',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.LIFESTYLE,
    icon: 'Plane',
    color: '#0ea5e9',
    sortOrder: 14,
    isActive: true
  },
  {
    id: 'cat_other_expense',
    name: 'Other Expenses',
    slug: 'other-expense',
    type: CATEGORY_TYPES.EXPENSE,
    group: CATEGORY_GROUPS.OTHER,
    icon: 'MoreHorizontal',
    color: '#6b7280',
    sortOrder: 99,
    isActive: true
  }
];

/**
 * Default income categories
 */
export const DEFAULT_INCOME_CATEGORIES = [
  {
    id: 'cat_salary',
    name: 'Salary / Wages',
    slug: 'salary',
    type: CATEGORY_TYPES.INCOME,
    group: CATEGORY_GROUPS.INCOME_SOURCES,
    icon: 'Briefcase',
    color: '#10b981',
    sortOrder: 1,
    isActive: true
  },
  {
    id: 'cat_freelance',
    name: 'Freelance / Contract',
    slug: 'freelance',
    type: CATEGORY_TYPES.INCOME,
    group: CATEGORY_GROUPS.INCOME_SOURCES,
    icon: 'FileText',
    color: '#8b5cf6',
    sortOrder: 2,
    isActive: true
  },
  {
    id: 'cat_business',
    name: 'Business Revenue',
    slug: 'business',
    type: CATEGORY_TYPES.INCOME,
    group: CATEGORY_GROUPS.INCOME_SOURCES,
    icon: 'Building',
    color: '#3b82f6',
    sortOrder: 3,
    isActive: true
  },
  {
    id: 'cat_investments',
    name: 'Investment Returns',
    slug: 'investments',
    type: CATEGORY_TYPES.INCOME,
    group: CATEGORY_GROUPS.INCOME_SOURCES,
    icon: 'TrendingUp',
    color: '#06b6d4',
    sortOrder: 4,
    isActive: true
  },
  {
    id: 'cat_refunds',
    name: 'Refunds / Reimbursements',
    slug: 'refunds',
    type: CATEGORY_TYPES.INCOME,
    group: CATEGORY_GROUPS.INCOME_SOURCES,
    icon: 'RefreshCw',
    color: '#22c55e',
    sortOrder: 5,
    isActive: true
  },
  {
    id: 'cat_other_income',
    name: 'Other Income',
    slug: 'other-income',
    type: CATEGORY_TYPES.INCOME,
    group: CATEGORY_GROUPS.INCOME_SOURCES,
    icon: 'MoreHorizontal',
    color: '#6b7280',
    sortOrder: 99,
    isActive: true
  }
];

/**
 * Default savings categories
 */
export const DEFAULT_SAVINGS_CATEGORIES = [
  {
    id: 'cat_emergency_fund',
    name: 'Emergency Fund',
    slug: 'emergency-fund',
    type: CATEGORY_TYPES.SAVINGS,
    group: CATEGORY_GROUPS.SAVINGS_GOALS,
    icon: 'Shield',
    color: '#ef4444',
    sortOrder: 1,
    isActive: true
  },
  {
    id: 'cat_vacation',
    name: 'Vacation',
    slug: 'vacation',
    type: CATEGORY_TYPES.SAVINGS,
    group: CATEGORY_GROUPS.SAVINGS_GOALS,
    icon: 'Plane',
    color: '#0ea5e9',
    sortOrder: 2,
    isActive: true
  },
  {
    id: 'cat_major_purchase',
    name: 'Major Purchase',
    slug: 'major-purchase',
    type: CATEGORY_TYPES.SAVINGS,
    group: CATEGORY_GROUPS.SAVINGS_GOALS,
    icon: 'ShoppingBag',
    color: '#8b5cf6',
    sortOrder: 3,
    isActive: true
  },
  {
    id: 'cat_retirement',
    name: 'Retirement',
    slug: 'retirement',
    type: CATEGORY_TYPES.SAVINGS,
    group: CATEGORY_GROUPS.SAVINGS_GOALS,
    icon: 'PiggyBank',
    color: '#10b981',
    sortOrder: 4,
    isActive: true
  }
];

// ============================================================================
// CORE API FUNCTIONS
// ============================================================================

/**
 * Get all categories for user from the central registry
 * FINLIST SSOT: All pages MUST use this function for category data
 * 
 * @param {string} userId 
 * @param {Object} options - { type: 'EXPENSE' | 'INCOME' | 'SAVINGS', activeOnly: boolean }
 * @returns {Promise<Array>} Array of category objects
 */
export async function getCategories(userId, options = {}) {
  try {
    if (!userId) {
      userId = auth.currentUser?.uid;
    }
    if (!userId) throw new Error('[CATEGORY REGISTRY] User not authenticated');

    const { type = null, activeOnly = true } = options;
    
    let q = query(
      getCategoryRegistryRef(userId),
      orderBy('sortOrder', 'asc')
    );

    if (type) {
      q = query(
        getCategoryRegistryRef(userId),
        where('type', '==', type),
        orderBy('sortOrder', 'asc')
      );
    }

    const snapshot = await getDocs(q);
    let categories = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
    }));

    // Filter active categories if requested
    if (activeOnly) {
      categories = categories.filter(cat => cat.isActive !== false);
    }

    // If no categories found, seed defaults and return them
    if (categories.length === 0) {
      logger.debug('[CATEGORY REGISTRY] No categories found, seeding defaults...');
      await seedDefaultCategories(userId);
      return getCategories(userId, options); // Recursive call after seeding
    }

    return categories;
  } catch (error) {
    logger.error('[CATEGORY REGISTRY] Error fetching categories:', error);
    // Return empty array instead of throwing to prevent UI crashes
    return [];
  }
}

/**
 * Get a single category by ID
 * FINLIST SSOT: Use this for category lookup by ID
 * 
 * @param {string} userId 
 * @param {string} categoryId 
 * @returns {Promise<Object|null>} Category object or null
 */
export async function getCategoryById(userId, categoryId) {
  try {
    if (!userId) {
      userId = auth.currentUser?.uid;
    }
    if (!userId) throw new Error('[CATEGORY REGISTRY] User not authenticated');
    if (!categoryId) return null;

    const docRef = getCategoryDocRef(userId, categoryId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      logger.warn(`[CATEGORY REGISTRY] Category not found: ${categoryId}`);
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt?.toDate?.() || docSnap.data().createdAt,
      updatedAt: docSnap.data().updatedAt?.toDate?.() || docSnap.data().updatedAt
    };
  } catch (error) {
    logger.error('[CATEGORY REGISTRY] Error fetching category by ID:', error);
    return null;
  }
}

/**
 * Create a new category in the registry
 * FINLIST SSOT: Only this function should create categories
 * 
 * @param {string} userId 
 * @param {Object} categoryData 
 * @returns {Promise<Object>} Created category
 */
export async function createCategory(userId, categoryData) {
  try {
    if (!userId) {
      userId = auth.currentUser?.uid;
    }
    if (!userId) throw new Error('[CATEGORY REGISTRY] User not authenticated');

    // Validate required fields
    if (!categoryData.name?.trim()) {
      throw new Error('Category name is required');
    }
    if (!categoryData.type || !Object.values(CATEGORY_TYPES).includes(categoryData.type)) {
      throw new Error('Valid category type is required');
    }

    // Generate ID if not provided
    const categoryId = categoryData.id || `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const category = {
      id: categoryId,
      name: categoryData.name.trim(),
      slug: categoryData.slug || categoryData.name.toLowerCase().replace(/\s+/g, '-'),
      type: categoryData.type,
      group: categoryData.group || CATEGORY_GROUPS.OTHER,
      icon: categoryData.icon || 'Tag',
      color: categoryData.color || '#6b7280',
      sortOrder: categoryData.sortOrder || 50,
      isActive: categoryData.isActive !== false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = getCategoryDocRef(userId, categoryId);
    await setDoc(docRef, category);

    logger.debug(`[CATEGORY REGISTRY] Category created: ${categoryId}`);
    return { ...category, id: categoryId };
  } catch (error) {
    logger.error('[CATEGORY REGISTRY] Error creating category:', error);
    throw error;
  }
}

/**
 * Update an existing category
 * 
 * @param {string} userId 
 * @param {string} categoryId 
 * @param {Object} updates 
 * @returns {Promise<Object>} Updated category
 */
export async function updateCategory(userId, categoryId, updates) {
  try {
    if (!userId) {
      userId = auth.currentUser?.uid;
    }
    if (!userId) throw new Error('[CATEGORY REGISTRY] User not authenticated');
    if (!categoryId) throw new Error('[CATEGORY REGISTRY] Category ID required');

    const docRef = getCategoryDocRef(userId, categoryId);
    
    // Filter allowed update fields
    const allowedFields = ['name', 'slug', 'group', 'icon', 'color', 'sortOrder', 'isActive'];
    const safeUpdates = {};
    for (const field of allowedFields) {
      if (updates[field] !== undefined) {
        safeUpdates[field] = updates[field];
      }
    }
    safeUpdates.updatedAt = serverTimestamp();

    await updateDoc(docRef, safeUpdates);

    logger.debug(`[CATEGORY REGISTRY] Category updated: ${categoryId}`);
    return getCategoryById(userId, categoryId);
  } catch (error) {
    logger.error('[CATEGORY REGISTRY] Error updating category:', error);
    throw error;
  }
}

/**
 * Delete (soft-delete by deactivating) a category
 * FINLIST SSOT: Categories are soft-deleted to preserve transaction references
 * 
 * @param {string} userId 
 * @param {string} categoryId 
 * @returns {Promise<boolean>}
 */
export async function deleteCategory(userId, categoryId) {
  try {
    if (!userId) {
      userId = auth.currentUser?.uid;
    }
    if (!userId) throw new Error('[CATEGORY REGISTRY] User not authenticated');
    if (!categoryId) throw new Error('[CATEGORY REGISTRY] Category ID required');

    // Soft delete by setting isActive to false
    await updateCategory(userId, categoryId, { isActive: false });

    logger.debug(`[CATEGORY REGISTRY] Category soft-deleted: ${categoryId}`);
    return true;
  } catch (error) {
    logger.error('[CATEGORY REGISTRY] Error deleting category:', error);
    throw error;
  }
}

/**
 * Seed default categories for a new user
 * FINLIST SSOT: Called automatically when user has no categories
 * 
 * @param {string} userId 
 * @returns {Promise<void>}
 */
export async function seedDefaultCategories(userId) {
  try {
    if (!userId) {
      userId = auth.currentUser?.uid;
    }
    if (!userId) throw new Error('[CATEGORY REGISTRY] User not authenticated');

    const batch = writeBatch(db);
    const allDefaults = [
      ...DEFAULT_EXPENSE_CATEGORIES,
      ...DEFAULT_INCOME_CATEGORIES,
      ...DEFAULT_SAVINGS_CATEGORIES
    ];

    for (const category of allDefaults) {
      const docRef = getCategoryDocRef(userId, category.id);
      batch.set(docRef, {
        ...category,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
    logger.debug(`[CATEGORY REGISTRY] Seeded ${allDefaults.length} default categories for user ${userId}`);
  } catch (error) {
    logger.error('[CATEGORY REGISTRY] Error seeding default categories:', error);
    throw error;
  }
}

/**
 * Subscribe to category changes (real-time updates)
 * FINLIST SSOT: Use this for real-time category updates in UI
 * 
 * @param {string} userId 
 * @param {Function} onUpdate - Callback with updated categories array
 * @param {Object} options - { type, activeOnly }
 * @returns {Function} Unsubscribe function
 */
export function subscribeToCategoryChanges(userId, onUpdate, options = {}) {
  if (!userId) {
    userId = auth.currentUser?.uid;
  }
  if (!userId) {
    logger.warn('[CATEGORY REGISTRY] Cannot subscribe without user ID');
    return () => {};
  }

  const { type = null, activeOnly = true } = options;

  let q = query(
    getCategoryRegistryRef(userId),
    orderBy('sortOrder', 'asc')
  );

  if (type) {
    q = query(
      getCategoryRegistryRef(userId),
      where('type', '==', type),
      orderBy('sortOrder', 'asc')
    );
  }

  const unsubscribe = onSnapshot(q, (snapshot) => {
    let categories = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
    }));

    if (activeOnly) {
      categories = categories.filter(cat => cat.isActive !== false);
    }

    onUpdate(categories);
  }, (error) => {
    logger.error('[CATEGORY REGISTRY] Subscription error:', error);
    onUpdate([]);
  });

  return unsubscribe;
}

// ============================================================================
// LEGACY MAPPING HELPERS
// ============================================================================

/**
 * Map legacy category ID/name to canonical categoryId (taxonomy format)
 * FINLIST SSOT: Delegates to categoryCore.js for single source of truth
 * 
 * NOTE: This now returns categoryTaxonomy IDs (e.g., 'groceries-food') instead of
 * the old cat_* format. This ensures consistent matching across the app.
 * 
 * @param {string} legacyCategoryRef - Old category ID, name, or slug
 * @returns {string} Canonical taxonomy ID or 'miscellaneous' as fallback
 */
export function mapLegacyToCanonicalCategoryId(legacyCategoryRef) {
  // SSOT: Delegate to categoryCore.js single source of truth
  return normalizeCategoryWithFallback(legacyCategoryRef, 'miscellaneous');
}

/**
 * Get categories grouped by type for UI dropdowns
 * @param {string} userId 
 * @returns {Promise<Object>} { expense: [], income: [], savings: [] }
 */
export async function getCategoriesGroupedByType(userId) {
  try {
    const allCategories = await getCategories(userId, { activeOnly: true });
    
    return {
      expense: allCategories.filter(c => c.type === CATEGORY_TYPES.EXPENSE),
      income: allCategories.filter(c => c.type === CATEGORY_TYPES.INCOME),
      savings: allCategories.filter(c => c.type === CATEGORY_TYPES.SAVINGS)
    };
  } catch (error) {
    logger.error('[CATEGORY REGISTRY] Error grouping categories:', error);
    return { expense: [], income: [], savings: [] };
  }
}

export default {
  CATEGORY_TYPES,
  CATEGORY_GROUPS,
  getCategories,
  getCategoryById,
  createCategory,
  updateCategory,
  deleteCategory,
  seedDefaultCategories,
  subscribeToCategoryChanges,
  mapLegacyToCanonicalCategoryId,
  getCategoriesGroupedByType
};
